import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { OnInteractionService } from '@df/ui/common/on-interaction.service';
import defaults from 'lodash-es/defaults';
import { Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, flatMap, map } from 'rxjs/operators';

export interface IIntersectionObserverOptions {
    root: any;
    rootMargin: string;
    threshold: number | number[];
}

export const VISIBILITY_OPTIONS: IIntersectionObserverOptions = {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: 0.0
};

export type TDynamicIntersectionOptions = {
    interaction: Partial<IIntersectionObserverOptions>;
    initial: Partial<IIntersectionObserverOptions>;
};

/**
 * Simple service to observe element visibility.
 * If refered element will be in view, service will call subscribed function and will stop observe.
 *
 * @usege
 * ```typescript
 * constructor(protected visibilityService: VisibilityService) {}
 *
 * this.visibilityService
 *   .isInView(this.elementRef)
 *   .pipe(
 *      filter(visible => visible),
 *      take(1)
 *   )
 *   .subscribe(() => this.createComponent());
 * ```
 *
 * @author Rafał Wolak <rafal@tomandco.co.uk>
 * @export
 * @class VisibilityService
 */
@Injectable({
    providedIn: 'root'
})
export class VisibilityService {
    // private pageVisible$: Observable<boolean>;

    constructor(
        @Inject(DOCUMENT) document: Document,
        private app: App,
        private onInteractionService: OnInteractionService
    ) {
        // this.pageVisible$ = concat(
        //     defer(() => of(!document.hidden)),
        //     fromEvent(document, 'visibilitychange').pipe(map(e => !document.hidden))
        // );
    }

    isInView(
        element: ElementRef | HTMLElement,
        options?: TDynamicIntersectionOptions | Partial<IIntersectionObserverOptions>
    ): Observable<boolean> {
        const el = element instanceof ElementRef ? element.nativeElement : element;

        const elementVisible$ = !this.app.isPlatformBrowser
            ? of(true)
            : new Observable(observer => {
                  let cleanup: () => void | undefined;
                  let onScrollSubscription: Subscription | undefined;

                  const setup = interacted => {
                      if (cleanup) {
                          cleanup();
                      }
                      const config = defaults(
                          options && (options as TDynamicIntersectionOptions).interaction
                              ? //   if dynamica options provided
                                interacted
                                  ? (options as TDynamicIntersectionOptions).interaction
                                  : (options as TDynamicIntersectionOptions).initial
                              : //   if no options or single instance
                                options || {},
                          VISIBILITY_OPTIONS
                      );
                      const intersectionObserver = new IntersectionObserver(entries => {
                          observer.next(entries);
                      }, config);
                      intersectionObserver.observe(el);
                      cleanup = () => intersectionObserver.disconnect();
                  };
                  if (options) {
                      onScrollSubscription = this.onInteractionService.scrollOnCurrentInteraction.subscribe(setup);
                  } else {
                      setup(false);
                  }

                  return () => {
                      if (cleanup) {
                          cleanup();
                      }
                      if (onScrollSubscription) {
                          onScrollSubscription.unsubscribe();
                      }
                  };
              }).pipe(
                  (flatMap as any)((entries: IntersectionObserverEntry[]) => entries),
                  map(entry => (entry as IntersectionObserverEntry).isIntersecting),
                  distinctUntilChanged()
              );

        return elementVisible$;

        /**
         * The logic below makes things visible only when window is focused.
         * Two problems with that, please do not restore:
         * 1. We do want things to load in non-focused tabs/windows
         * 2. That only makes sense when you want to prevent images from loading from anotether window
         * but for element that base logic on what is in viewport it causes strange effects when you
         * leave the tab
         */
        // const inView$ = combineLatest([this.pageVisible$, elementVisible$]).pipe(distinctUntilChanged());
        // const result$ = inView$.pipe(map(([pageVisible, elementVisible]) => pageVisible && elementVisible));
        // return result$;
    }
}
